import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useUser, useAuth as useClerkAuth, useOrganization } from "@clerk/react";
import { setClerkTokenGetter } from "@/lib/api";
import api from "@/lib/api";
import { errorTracker } from "@/lib/error-tracking";

interface User {
    id: string;
    email: string;
    name: string;
    role: "admin" | "manager" | "employee";
    imageUrl?: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    orgId: string | null;
    orgName: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const { user: clerkUser, isLoaded: userLoaded, isSignedIn } = useUser();
    const { getToken } = useClerkAuth();
    const { organization } = useOrganization();
    const [dbRole, setDbRole] = useState<User["role"] | null>(null);

    // Wire up the Clerk token getter so the API client can attach JWTs
    useEffect(() => {
        setClerkTokenGetter(getToken);
    }, [getToken]);

    // Fetch the real role from the DB once the user is authenticated
    useEffect(() => {
        if (!userLoaded || !isSignedIn) {
            setDbRole(null);
            return;
        }
        const fetchRole = async () => {
            try {
                const { data } = await api.get<{ role: string }>("/users/me");
                if (data?.role) {
                    setDbRole(data.role as User["role"]);
                }
            } catch {
                // Silently fall back to Clerk metadata role
            }
        };
        fetchRole();
    }, [userLoaded, isSignedIn]);

    const clerkRole = (clerkUser?.publicMetadata?.role as User["role"]) ?? "employee";

    const user: User | null =
        userLoaded && isSignedIn && clerkUser
            ? {
                id: clerkUser.id,
                email: clerkUser.primaryEmailAddress?.emailAddress ?? "",
                name: clerkUser.fullName ?? clerkUser.firstName ?? "User",
                role: dbRole ?? clerkRole,
                imageUrl: clerkUser.imageUrl,
            }
            : null;

    const value: AuthContextType = {
        user,
        isAuthenticated: !!isSignedIn,
        isLoading: !userLoaded,
        orgId: organization?.id ?? null,
        orgName: organization?.name ?? null,
    };

    // ── Sentry user context ─────────────────────────
    useEffect(() => {
        if (user) {
            errorTracker.setUser(user.id, user.email, organization?.id ?? "unknown");
        } else {
            errorTracker.clearUser();
        }
    }, [user, organization?.id]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}